import React from "react";
import * as LucideIcons from "lucide-react";

/**
 * Tile — single bento card on the Operational Base home grid.
 *
 * Anatomy follows /app/design_guidelines.md §4.1:
 *   ┌─────────────────────────────────────┐
 *   │  [icon]                       [dot] │   ← top row
 *   │                                     │
 *   │  Headline metric                    │   ← Outfit, large
 *   │  Plain-language context             │   ← Manrope, secondary
 *   └─────────────────────────────────────┘
 *
 * 5-second comprehension test: every tile communicates one operational
 * concept · one primary metric · one optional action.
 */
export const Tile = ({
    moduleName,
    icon = "Square",
    title,
    metric,
    context,
    severity = "operational",
    onTap,
    span = "default",
    testId,
    children,
}) => {
    const Icon = LucideIcons[_iconName(icon)] || LucideIcons.Square;

    const spanClass = span === "wide"
        ? "md:col-span-2"
        : span === "tall"
            ? "md:row-span-2"
            : "";

    const Component = onTap ? "button" : "div";

    return (
        <Component
            data-testid={testId || `ob-tile-${moduleName || title}`}
            data-module={moduleName}
            onClick={onTap}
            className={`ob-card ob-tile text-left ${onTap ? "ob-card-tappable" : ""} ${spanClass}`}
        >
            <div className="flex items-start justify-between">
                <div className="ob-tile-icon" aria-hidden="true">
                    <Icon size={20} strokeWidth={1.5} />
                </div>
                {severity && (
                    <span
                        className={`ob-tile-status ${severity}`}
                        aria-hidden="true"
                        data-testid={`ob-tile-status-${severity}`}
                    />
                )}
            </div>

            <div className="flex-1 flex flex-col justify-end gap-1">
                {title && (
                    <p className="ob-eyebrow" data-testid="ob-tile-title">
                        {title}
                    </p>
                )}
                {metric != null && (
                    <p className="ob-tile-metric" data-testid="ob-tile-metric">
                        {metric}
                    </p>
                )}
                {context && (
                    <p className="ob-tile-context" data-testid="ob-tile-context">
                        {context}
                    </p>
                )}
                {children}
            </div>
        </Component>
    );
};

function _iconName(name) {
    // Convert "shield-check" → "ShieldCheck" so we can read off lucide-react.
    if (!name) return "Square";
    return name
        .split("-")
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join("");
}
